import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import FamilyDetailHeader from './FamilyDetailHeader';
import { containerVariants, itemVariants } from '@/utils/animations';
import { Family, Parent, Student } from '@/types/models';
import AddParentDialog from '@/components/families/AddParentDialog';
import AddStudentDialog from '@/components/families/AddStudentDialog';
import EditParentDialog from '@/components/families/EditParentDialog';

interface FamilyDetailLayoutProps {
  family: Family | null;
  parents: Parent[];
  students: Student[];
  activeTab: string;
  setActiveTab: (tab: string) => void;
  handleBackClick: () => void;
  handleAddParentClick: () => void;
  handleAddStudentClick: () => void;
  handleParentAdded: (parentData: any, continueToDetails?: boolean) => Promise<void>;
  handleStudentAdded: (studentData: any, continueToDetails?: boolean) => Promise<void>;
  isAddParentDialogOpen: boolean;
  setIsAddParentDialogOpen: (isOpen: boolean) => void;
  isAddStudentDialogOpen: boolean;
  setIsAddStudentDialogOpen: (isOpen: boolean) => void;
  isAddingParent: boolean;
  isAddingStudent: boolean;
  editingParent: Parent | null;
  isEditParentDialogOpen: boolean;
  setIsEditParentDialogOpen: (isOpen: boolean) => void;
  handleParentUpdate: (parentId: string, updatedData: Partial<Parent>) => void;
  handleDeleteParent?: (parent: Parent) => void;
  familyId: string;
  children: React.ReactNode;
}

const FamilyDetailLayout: React.FC<FamilyDetailLayoutProps> = ({
  family,
  parents,
  students,
  activeTab,
  setActiveTab,
  handleBackClick,
  handleAddParentClick,
  handleAddStudentClick,
  handleParentAdded,
  handleStudentAdded,
  isAddParentDialogOpen,
  setIsAddParentDialogOpen,
  isAddStudentDialogOpen,
  setIsAddStudentDialogOpen,
  isAddingParent,
  isAddingStudent,
  editingParent,
  isEditParentDialogOpen,
  setIsEditParentDialogOpen,
  handleParentUpdate,
  handleDeleteParent,
  familyId,
  children
}) => {
  if (!family) {
    return (
      <div className="container py-8">
        <div className="flex justify-between items-center mb-6">
          <Button variant="ghost" onClick={handleBackClick} className="gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back to Families
          </Button>
        </div>
        <div className="flex justify-center items-center h-40">
          <p className="text-muted-foreground">Loading family details...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="container py-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <FamilyDetailHeader 
        familyName={family.name}
        onBackClick={handleBackClick}
        onAddParentClick={handleAddParentClick}
        onAddStudentClick={handleAddStudentClick}
        onManageBillingClick={() => setActiveTab('billing')}
        activeTab={activeTab}
      />

      <motion.div variants={itemVariants}>
        <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="parents">Parents ({parents.length})</TabsTrigger>
            <TabsTrigger value="students">Students ({students.length})</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
          </TabsList>
          
          {children}
        </Tabs>
      </motion.div>
      
      <AddParentDialog
        open={isAddParentDialogOpen}
        onOpenChange={setIsAddParentDialogOpen}
        onParentAdded={handleParentAdded}
        familyId={familyId}
        isLoading={isAddingParent}
        familyName={family?.name}
      />
      
      <AddStudentDialog 
        open={isAddStudentDialogOpen}
        onOpenChange={setIsAddStudentDialogOpen}
        onStudentAdded={handleStudentAdded}
        familyId={familyId}
        isLoading={isAddingStudent}
        familyName={family?.name}
      />

      <EditParentDialog
        isOpen={isEditParentDialogOpen}
        onClose={() => setIsEditParentDialogOpen(false)}
        parent={editingParent}
        onSubmit={handleParentUpdate}
        onDelete={handleDeleteParent}
      />
    </motion.div>
  );
};

export default FamilyDetailLayout;
